/**
 * Registro del service worker (public/sw.js) y de "la caja offline".
 *
 * El modo offline vive en UN dispositivo: la PC de la caja con el Print Agent (ver
 * docs/modo-offline.md). Ese dispositivo se reconoce solo: la primera vez que la app
 * encuentra al agente en su flujo normal (config/printConfig, que Comandear y Cobrar
 * consultan al abrirse) queda marcado aquí, y desde entonces el service worker se
 * registra con `?shell=1` para guardar la app y poder abrirla sin red. Los demás
 * dispositivos siguen como siempre: el mismo sw.js, solo con push.
 *
 * No hay un sondeo propio al arrancar a propósito: correría en TODOS los
 * dispositivos sin marca (teléfonos de meseros, antes del login), tardaría hasta 6 s
 * cuando falla, y Chrome puede pedir permiso de "red local" por cada petición a
 * localhost.
 *
 * La marca no se quita si un día el agente no responde (estaba apagado, se reinició
 * la PC): perder la app guardada justo el día que falte el internet es peor que
 * conservarla de más.
 */
export const OFFLINE_CAJA_DEVICE_KEY = 'offlineCajaDevice';

export const isOfflineCajaDevice = (): boolean => {
  try {
    return localStorage.getItem(OFFLINE_CAJA_DEVICE_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * URL con la que se registra el service worker. Push y arranque sin red la
 * comparten: si cada uno registrara la suya, el último en registrarse le quitaría
 * el scope al otro.
 */
export const getServiceWorkerUrl = (): string =>
  `${process.env.PUBLIC_URL || ''}/sw.js${isOfflineCajaDevice() ? '?shell=1' : ''}`;

export const isServiceWorkerSupported = (): boolean =>
  typeof navigator !== 'undefined' && 'serviceWorker' in navigator;

// Solo en producción: en desarrollo una app guardada por el service worker taparía
// los cambios del dev server.
const shellAllowed = (): boolean =>
  process.env.NODE_ENV === 'production' && isServiceWorkerSupported();

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isServiceWorkerSupported()) return null;
  try {
    // updateViaCache 'none': el navegador revisa sw.js (y lo que importa push-sw.js)
    // contra el servidor en cada chequeo, sin la caché HTTP de por medio.
    return await navigator.serviceWorker.register(getServiceWorkerUrl(), { updateViaCache: 'none' });
  } catch (err) {
    console.error('No se pudo registrar el service worker:', err);
    return null;
  }
}

/** Se llama una vez al arrancar. Sin marca no hace nada: ni sondea ni registra. */
export async function initOfflineShell(): Promise<void> {
  if (!shellAllowed() || !isOfflineCajaDevice()) return;
  await registerServiceWorker();
  await requestPersistentStorage();
}

/**
 * La llama config/printConfig cuando el Print Agent responde. La primera vez marca
 * el equipo como caja offline y registra el service worker en ese momento, sin
 * esperar al siguiente arranque.
 */
export function markOfflineCajaDevice(): void {
  if (isOfflineCajaDevice()) return;
  try {
    localStorage.setItem(OFFLINE_CAJA_DEVICE_KEY, '1');
  } catch {
    return; // Sin localStorage no hay forma de recordar la marca.
  }
  if (shellAllowed()) {
    void registerServiceWorker().then(requestPersistentStorage);
  }
}

/**
 * Pide que el navegador no borre la app guardada ni el catálogo de IndexedDB cuando
 * le falte espacio. Chrome lo concede solo, sin preguntar, a un sitio que el usuario
 * usa seguido. Si no lo concede, todo funciona igual con el riesgo de que se borre.
 */
async function requestPersistentStorage(): Promise<void> {
  try {
    if (navigator.storage?.persist && !(await navigator.storage.persisted())) {
      await navigator.storage.persist();
    }
  } catch {
    // Sin la API: se queda como almacenamiento normal.
  }
}
